// Reads the marketplace's public `GET /api/config` at app bootstrap (see
// src/config/runtime.ts).
//
// The vault deliberately has no payments switch of its own. Whether the
// consumer payments surface exists is a property of the marketplace
// deployment — it either has a payment-service configured or it does not —
// and the vault reaches payment-service only through the marketplace's
// /api/v1/payments/* proxy. A second local flag could disagree with it, and
// the failure that produces is a Billing section that renders while every
// call behind it 404s. So the marketplace is the single source and the vault
// reads it.
//
// Mirrors gateway-info.ts: bounded timeout, failures are not cached so the
// next boot retries, and an unreachable marketplace resolves to null rather
// than throwing. Null means UNKNOWN, not off — see loadRuntimeConfig for why
// that distinction matters to a subscriber.

const TTL_MS = 5 * 60 * 1000;
// Matches the gateway-info budget. The dev cluster answers /api/config in
// well under this; the cap only bounds a genuinely dead endpoint.
const FETCH_TIMEOUT_MS = 10_000;

export interface MarketplaceConfig {
  paymentsEnabled: boolean;
}

interface CachedEntry extends MarketplaceConfig {
  at: number;
}

let cache: CachedEntry | null = null;

export function resetMarketplaceConfigCacheForTests(): void {
  cache = null;
}

export async function getMarketplaceConfig(
  marketplaceBaseUrl: string | undefined,
  fetchImpl: typeof fetch = fetch,
  now: () => number = Date.now,
): Promise<MarketplaceConfig | null> {
  if (!marketplaceBaseUrl) return null;
  if (cache && now() - cache.at < TTL_MS) return { paymentsEnabled: cache.paymentsEnabled };

  try {
    const r = await fetchImpl(marketplaceBaseUrl.replace(/\/+$/, '') + '/api/config', {
      signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
    });
    if (!r.ok) return null;
    const body = (await r.json()) as { paymentsEnabled?: unknown };
    // Anything other than a real boolean is unknown, not a value. Never
    // coerce — a truthy string would silently become "payments on".
    if (typeof body.paymentsEnabled !== 'boolean') return null;
    cache = { paymentsEnabled: body.paymentsEnabled, at: now() };
    return { paymentsEnabled: body.paymentsEnabled };
  } catch {
    return null;
  }
}
